import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Plus, Edit, Filter, Eye, UserCheck, UserX, Key } from 'lucide-react';
import { userService } from '@/services/userService';
import { User, UserListParams } from '@/types/user';
import { Button, Input } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { UserForm } from './UserForm';
import { UserDetail } from './UserDetail';
import { PasswordResetDialog } from './PasswordResetDialog';
import rgfLauncher from '@/assets/rgf-launcher.png';

export const UserList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [selectedRole, setSelectedRole] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [filters, setFilters] = useState<UserListParams>({
    page: 1,
    page_size: 10,
  });
  const { showError, showSuccess } = useNotification();

  // Available programs for filtering
  const programs = [
    { value: '', label: 'All Programs' },
    { value: 'TBTP', label: 'The Billion Tree Project' },
    { value: 'WM', label: 'Waste Management' },
    { value: 'WE', label: 'Women Equity' },
    { value: 'RGSH', label: 'RGS Hockey' },
    { value: 'SD', label: 'Sports for Development' },
    { value: 'LL', label: 'Learn Labs' },
    { value: 'RF', label: 'Regenerative Farming' },
    { value: 'YOGA', label: 'Yoga Program' },
  ];

  // Get roles from API
  const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useQuery({
    queryKey: ['application-roles'],
    queryFn: () => userService.getApplicationRoles(),
    retry: 1, // Reduce retries for faster debugging
  });


  // Update filters when search, page, program, role, or status changes
  useEffect(() => {
    const newFilters = {
      page: currentPage,
      page_size: 10,
      q: searchQuery?.trim() || undefined,
      program: selectedProgram || undefined,
      role: selectedRole || undefined,
      status: selectedStatus || undefined,
    };
    
    setFilters(newFilters);
  }, [searchQuery, currentPage, selectedProgram, selectedRole, selectedStatus]);

  const {
    data: usersData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['users', filters],
    queryFn: () => userService.getUsers(filters),
    placeholderData: (previousData) => previousData, // Updated API for React Query v5
  });

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleRoleChange = (value: string) => {
    setSelectedRole(value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleProgramChange = (value: string) => {
    setSelectedProgram(value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setCurrentPage(1); // Reset to first page when filtering
  };


  const handleToggleUserStatus = async (user: User) => {
    const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    const action = newStatus === 'Active' ? 'activate' : 'deactivate';
    
    if (!confirm(`Are you sure you want to ${action} user ${user.first_name} ${user.last_name}?`)) {
      return;
    }

    try {
      const response = await userService.updateUser({
        db_id: user.db_id,
        fusion_auth_user_id: user.fusion_auth_user_id,
        status: newStatus,
      });
      showSuccess(`User ${newStatus}`, response.message || `User ${user.first_name} ${user.last_name} has been ${action}d successfully.`);
      refetch();
    } catch (error: any) {
      showError(`${action} Failed`, error.message || `Failed to ${action} user`);
    }
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowUserForm(true);
  };

  const handleResetPassword = (user: User) => {
    setSelectedUser(user);
    setShowPasswordReset(true);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowUserForm(true);
  };

  const handleFormSuccess = () => {
    refetch();
    setShowUserForm(false);
    setSelectedUser(null);
  };

  const handleDetailEdit = () => {
    setShowUserDetail(false);
    setShowUserForm(true);
  };

  const handlePasswordResetSuccess = () => {
    refetch();
    setShowPasswordReset(false);
    setSelectedUser(null);
  };

  if (error) {
    const isValidationError = (error as any)?.isValidationError;
    const errorMessage = (error as any)?.message || 'Failed to load users';
    
    return (
      <div className="text-center py-12">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <span className="text-red-600">⚠️</span>
        </div>
        <h3 className="text-lg font-medium text-rgf-gray-900 mb-2">
          Error Loading Users
        </h3>
        <p className="text-rgf-gray-600 mb-4">
          {errorMessage}
        </p>
        {isValidationError && (error as any)?.validationData && (
          <div className="mb-4 text-left max-w-md mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm font-medium text-red-800 mb-2">Additional Details:</p>
              <p className="text-sm text-red-700">
                {(error as any).validationData.message}
              </p>
            </div>
          </div>
        )}
        <Button onClick={() => refetch()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-rgf-gray-900 sm:text-3xl">
            User Management
          </h1>
          <p className="mt-1 text-sm text-rgf-gray-500">
            Manage users, their roles, and permissions across RGF programs.
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Button
            icon={<Plus className="h-4 w-4" />}
            onClick={handleCreateUser}
          >
            Add User
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="card-body">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search users by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  startIcon={<Search className="h-4 w-4" />}
                />
              </div>
              <Button 
                variant="secondary" 
                icon={<Filter className="h-4 w-4" />}
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>

            {/* Collapsible Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-rgf-gray-200">
                {/* Program Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Program
                  </label>
                  <select
                    value={selectedProgram}
                    onChange={(e) => handleProgramChange(e.target.value)}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {programs.map((program) => (
                      <option key={program.value} value={program.value}>
                        {program.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={selectedRole}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                    disabled={rolesLoading}
                  >
                    <option value="">
                      {rolesLoading ? 'Loading roles...' : 'All Roles'}
                    </option>
                    {rolesError ? (
                      <option disabled>Error loading roles</option>
                    ) : (
                      rolesData?.roles?.map((role: string | { id: string; title: string; description?: string }, index: number) => {
                        // Handle both string and object formats
                        const roleValue = typeof role === 'string' ? role : role.id;  // Use ID for API call
                        const roleLabel = typeof role === 'string' ? role : role.title; // Show title to user
                        const roleKey = typeof role === 'string' ? role : role.id || role.title;
                        
                        return (
                          <option key={roleKey || index} value={roleValue}>
                            {roleLabel}
                          </option>
                        );
                      })
                    )}
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    <option value="">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSelectedProgram('');
                      setSelectedRole('');
                      setSelectedStatus('Active');
                      setSearchQuery('');
                      setCurrentPage(1);
                    }}
                    className="w-full"
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body p-0">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="mb-4">
                <img
                  className="h-12 w-12 mx-auto animate-pulse"
                  src={rgfLauncher}
                  alt="Loading"
                />
              </div>
              <p className="text-rgf-gray-600">Loading users...</p>
            </div>
          ) : !usersData?.list?.length ? (
            <div className="p-8 text-center">
              <p className="text-rgf-gray-600">No users found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-rgf-gray-200">
                <thead className="bg-rgf-gray-50">
                  <tr>
                    <th className="table-header">Name</th>
                    <th className="table-header">Email</th>
                    <th className="table-header">Phone</th>
                    <th className="table-header">Roles</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Joining Date</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-rgf-gray-200">
                  {usersData.list.map((user: User) => (
                    <tr key={user.db_id} className="hover:bg-rgf-gray-50">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-10 w-10 bg-rgf-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-rgf-primary-700">
                              {user.first_name?.[0]}{user.last_name?.[0]}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-rgf-gray-900">
                              {user.first_name} {user.middle_name} {user.last_name}
                            </div>
                            <div className="text-sm text-rgf-gray-500">
                              ID: {user.employee_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-rgf-gray-900">{user.email}</div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-rgf-gray-900">{user.phone_number}</div>
                      </td>
                      <td className="table-cell">
                        <div className="flex flex-wrap gap-1">
                          {user.roles.slice(0, 2).map((role: string, index: number) => (
                            <span key={index} className="badge badge-info text-xs">
                              {role}
                            </span>
                          ))}
                          {user.roles.length > 2 && (
                            <span className="text-xs text-rgf-gray-500">
                              +{user.roles.length - 2} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${
                          user.status === 'Active' ? 'badge-success' : 'badge-error'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-rgf-gray-900">
                          {new Date(user.joining_date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Eye className="h-4 w-4" />}
                            onClick={() => handleViewUser(user)}
                            title="View Details"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Edit className="h-4 w-4" />}
                            onClick={() => handleEditUser(user)}
                            title="Edit User"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Key className="h-4 w-4" />}
                            onClick={() => handleResetPassword(user)}
                            title="Set New Password"
                            className="text-blue-600 hover:text-blue-800"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={user.status === 'Active' ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                            onClick={() => handleToggleUserStatus(user)}
                            title={user.status === 'Active' ? 'Deactivate User' : 'Activate User'}
                            className={user.status === 'Active' ? 'text-orange-600 hover:text-orange-800' : 'text-green-600 hover:text-green-800'}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {usersData && usersData.total > 0 && (
          <div className="px-6 py-3 bg-rgf-gray-50 border-t border-rgf-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-rgf-gray-700">
                Showing {((usersData.page - 1) * usersData.page_size) + 1} to{' '}
                {Math.min(usersData.page * usersData.page_size, usersData.total)} of{' '}
                {usersData.total} results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-rgf-gray-700">
                  Page {currentPage}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage >= Math.ceil(usersData.total / usersData.page_size)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Form Modal */}
      <UserForm
        user={selectedUser}
        isOpen={showUserForm}
        onClose={() => {
          setShowUserForm(false);
          setSelectedUser(null);
        }}
        onSuccess={handleFormSuccess}
      />

      {/* User Detail Modal */}
      <UserDetail
        user={selectedUser}
        isOpen={showUserDetail}
        onClose={() => {
          setShowUserDetail(false);
          setSelectedUser(null);
        }}
        onEdit={handleDetailEdit}
      />

      {/* Password Reset Modal */}
      <PasswordResetDialog
        user={selectedUser}
        isOpen={showPasswordReset}
        onClose={() => {
          setShowPasswordReset(false);
          setSelectedUser(null);
        }}
        onSuccess={handlePasswordResetSuccess}
      />
    </div>
  );
};